import React, { useMemo } from "react";
import { Grid, Typography } from "@material-ui/core";
import intl from "react-intl-universal";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LegendValueFormatter,
  TooltipFormatter,
} from "recharts";
import { makeStyles } from "@material-ui/core/styles";

import { IWithballData } from "types";
import {
  groupWithBallByPosition,
  groupWithBallByFromPlayer,
  calculatePecentage,
} from "utils";
import { StatisticsBoard } from "components/StatisticsBoard";
import { BAR_COLOR } from "MuiProvider/colors";

interface IDribblePanel {
  dataArray: IWithballData[];
}

const useStyles = makeStyles(() => ({
  bottomAlignGrid: {
    position: "relative",
  },
  bottomAlignContent: {
    position: "absolute",
    bottom: 0,
    height: "auto !important",
  },
}));

const ASPECT = 2;
const CHART_WIDTH = "100%";
const BAR_SIZE = 20;

export const DribblePanel = ({ dataArray }: IDribblePanel) => {
  const classes = useStyles();

  const { summary, matrix } = useMemo(() => {
    return {
      summary: groupWithBallByPosition(dataArray),
      matrix: groupWithBallByFromPlayer(dataArray),
    };
  }, [dataArray]);
  const succInSelfHalf = summary.selfHalf.filter((dribble) => dribble.succeed)
    .length;
  const succInOtherHalf = summary.otherHalf.filter((dribble) => dribble.succeed)
    .length;

  const legendFormatter: LegendValueFormatter = (value, entry) => {
    let label = "";
    switch (value) {
      case "succ":
        label = intl.get("MATCH_TEAM_OVERVIEW_DRIBBLE_SUCC");
        break;
      case "total":
        label = intl.get("MATCH_TEAM_OVERVIEW_DRIBBLE_ATTEMPT_TOTAL");
        break;
    }
    return <span style={{ color: entry?.color }}>{label}</span>;
  };

  const tooltipFormatter: TooltipFormatter = (value, name) => {
    let label = "";
    switch (name) {
      case "succ":
        label = intl.get("MATCH_TEAM_OVERVIEW_DRIBBLE_SUCC");
        break;
      case "total":
        label = intl.get("MATCH_TEAM_OVERVIEW_DRIBBLE_ATTEMPT_TOTAL");
        break;
    }
    return [value, label];
  };

  const chartData = Object.getOwnPropertyNames(matrix).map((property) => {
    return {
      name: matrix[property][0].fromName,
      total: matrix[property].length,
      succ: matrix[property].filter((data) => data.succeed).length,
    };
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} lg={4}>
        <Typography>{`${intl.get("STATS_POS_SELF")}`}</Typography>
        <Typography>{`${intl.get(
          "MATCH_TEAM_OVERVIEW_DRIBBLE_ATTEMPT_TOTAL"
        )}: ${summary.selfHalf.length}`}</Typography>
        <Typography>{`${intl.get(
          "MATCH_TEAM_OVERVIEW_DRIBBLE_SUCC"
        )}: ${succInSelfHalf} (${calculatePecentage(
          succInSelfHalf,
          summary.selfHalf.length
        )})`}</Typography>
        <StatisticsBoard data={summary.selfHalf} />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <Typography>{`${intl.get("STATS_POS_OTHER")}`}</Typography>
        <Typography>{`${intl.get(
          "MATCH_TEAM_OVERVIEW_DRIBBLE_ATTEMPT_TOTAL"
        )}: ${summary.otherHalf.length}`}</Typography>
        <Typography>{`${intl.get(
          "MATCH_TEAM_OVERVIEW_DRIBBLE_SUCC"
        )}: ${succInOtherHalf} (${calculatePecentage(
          succInOtherHalf,
          summary.otherHalf.length
        )})`}</Typography>
        <StatisticsBoard data={summary.otherHalf} />
      </Grid>
      {chartData.length > 0 && (
        <Grid item xs={12} md={6} lg={4} className={classes.bottomAlignGrid}>
          <ResponsiveContainer
            width={CHART_WIDTH}
            aspect={ASPECT}
            className={classes.bottomAlignContent}
          >
            <BarChart
              data={chartData}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            >
              <XAxis dataKey="name" angle={-45} interval={0} />
              <YAxis />
              <Tooltip formatter={tooltipFormatter} />
              <Legend formatter={legendFormatter} />
              <Bar dataKey="succ" fill={BAR_COLOR.succ} barSize={BAR_SIZE} />
              <Bar dataKey="total" fill={BAR_COLOR.total} barSize={BAR_SIZE} />
            </BarChart>
          </ResponsiveContainer>
        </Grid>
      )}
    </Grid>
  );
};
